import {
  Card,
  Box,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Alert,
  useTheme,
  useMediaQuery,
  CircularProgress,
  IconButton,
  Tooltip,
} from "@mui/material";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import axios from "../utils/axios-instance";
import { ServerRoutes } from "../routes";
import { CourseSeason } from "../types";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";

const fetchCourseSeasons = async (courseId: number) => {
  const response = await axios.get<CourseSeason[]>(
    `${ServerRoutes.courseSeasons}?courseId=${courseId}`
  );
  return response.data;
};

const emptySeason = {
  name: "",
  description: "",
  duration: "",
  priority: "",
};

export function CourseSeasonsSection() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const { courseId } = useParams();
  const numericCourseId = Number(courseId);

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedSeason, setSelectedSeason] = useState<CourseSeason | null>(null);
  const [formValues, setFormValues] = useState(emptySeason);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">(
    "success"
  );

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["course-seasons", numericCourseId],
    queryFn: () => fetchCourseSeasons(numericCourseId),
    enabled: Number.isFinite(numericCourseId),
  });

  const seasons = data ?? [];
  const courseName = seasons[0]?.course?.name ?? `Course ${courseId}`;

  const openCreateDialog = () => {
    setFormValues(emptySeason);
    setIsCreateOpen(true);
  };

  const openEditDialog = (season: CourseSeason) => {
    setSelectedSeason(season);
    setFormValues({
      name: season.name ?? "",
      description: season.description ?? "",
      duration: season.duration?.toString() ?? "",
      priority: season.priority?.toString() ?? "",
    });
    setIsEditOpen(true);
  };

  const openDeleteDialog = (season: CourseSeason) => {
    setSelectedSeason(season);
    setIsDeleteOpen(true);
  };

  const closeCreateDialog = () => {
    if (!isSubmitting) {
      setIsCreateOpen(false);
    }
  };

  const closeEditDialog = () => {
    if (!isSubmitting) {
      setIsEditOpen(false);
    }
  };

  const closeDeleteDialog = () => {
    if (!isSubmitting) {
      setIsDeleteOpen(false);
    }
  };

  const handleFormChange = (field: keyof typeof emptySeason) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormValues((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleCreateSeason = async () => {
    setIsSubmitting(true);
    try {
      await axios.post(
        `${ServerRoutes.courseSeasons}?courseId=${numericCourseId}`,
        {
          name: formValues.name.trim(),
          description: formValues.description.trim(),
          duration: Number(formValues.duration) || 0,
          priority: Number(formValues.priority) || 0,
        }
      );
      setSnackbarMessage("Season created successfully.");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      setIsCreateOpen(false);
      refetch();
    } catch (error) {
      setSnackbarMessage("Failed to create season.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditSeason = async () => {
    if (!selectedSeason) return;
    setIsSubmitting(true);
    try {
      await axios.patch(`${ServerRoutes.courseSeasons}/${selectedSeason.id}`, {
        courseId: numericCourseId,
        name: formValues.name.trim(),
        description: formValues.description.trim(),
        duration: Number(formValues.duration) || 0,
        priority: Number(formValues.priority) || 0,
      });
      setSnackbarMessage("Season updated successfully.");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      setIsEditOpen(false);
      setSelectedSeason(null);
      refetch();
    } catch (error) {
      setSnackbarMessage("Failed to update season.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteSeason = async () => {
    if (!selectedSeason) return;
    setIsSubmitting(true);
    try {
      await axios.delete(`${ServerRoutes.courseSeasons}/${selectedSeason.id}`);
      setSnackbarMessage("Season deleted successfully.");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      setIsDeleteOpen(false);
      setSelectedSeason(null);
      refetch();
    } catch (error) {
      setSnackbarMessage("Failed to delete season.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  return (
    <Card sx={{ p: isMobile ? 2 : 4 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 3,
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Box>
          <Typography variant="h5" fontWeight={700}>
            {courseName}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Seasons
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button variant="outlined" onClick={() => navigate("/courses")}>
            Back to Courses
          </Button>
          <Button variant="contained" onClick={openCreateDialog}>
            Add Season
          </Button>
        </Box>
      </Box>

      <Paper elevation={2}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Duration</TableCell>
              <TableCell>Priority</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : isError ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  Failed to load seasons.
                </TableCell>
              </TableRow>
            ) : seasons.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  No seasons found.
                </TableCell>
              </TableRow>
            ) : (
              seasons.map((season) => (
                <TableRow key={season.id}>
                  <TableCell>{season.name}</TableCell>
                  <TableCell>{season.description}</TableCell>
                  <TableCell>{season.duration}</TableCell>
                  <TableCell>{season.priority}</TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
                      <Tooltip title="View Items">
                        <IconButton
                          onClick={() =>
                            navigate(`/courses/${numericCourseId}/seasons/${season.id}`)
                          }
                          size="small"
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton onClick={() => openEditDialog(season)} size="small">
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          onClick={() => openDeleteDialog(season)}
                          size="small"
                          color="error"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Paper>

      <Dialog open={isCreateOpen} onClose={closeCreateDialog} fullWidth maxWidth="sm">
        <DialogTitle>Add Season</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <TextField
              label="Name"
              value={formValues.name}
              onChange={handleFormChange("name")}
              fullWidth
            />
            <TextField
              label="Description"
              value={formValues.description}
              onChange={handleFormChange("description")}
              fullWidth
              multiline
              rows={3}
            />
            <TextField
              label="Duration (minutes)"
              value={formValues.duration}
              onChange={handleFormChange("duration")}
              fullWidth
              type="number"
            />
            <TextField
              label="Priority"
              value={formValues.priority}
              onChange={handleFormChange("priority")}
              fullWidth
              type="number"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCreateDialog} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateSeason}
            disabled={isSubmitting}
          >
            {isSubmitting ? "Creating..." : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={isEditOpen} onClose={closeEditDialog} fullWidth maxWidth="sm">
        <DialogTitle>Edit Season</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <TextField
              label="Name"
              value={formValues.name}
              onChange={handleFormChange("name")}
              fullWidth
            />
            <TextField
              label="Description"
              value={formValues.description}
              onChange={handleFormChange("description")}
              fullWidth
              multiline
              rows={3}
            />
            <TextField
              label="Duration (minutes)"
              value={formValues.duration}
              onChange={handleFormChange("duration")}
              fullWidth
              type="number"
            />
            <TextField
              label="Priority"
              value={formValues.priority}
              onChange={handleFormChange("priority")}
              fullWidth
              type="number"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEditDialog} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleEditSeason}
            disabled={isSubmitting}
          >
            {isSubmitting ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={isDeleteOpen} onClose={closeDeleteDialog} fullWidth maxWidth="xs">
        <DialogTitle>Delete Season</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{selectedSeason?.name}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteSeason}
            disabled={isSubmitting}
          >
            {isSubmitting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Card>
  );
}

